import React, { useEffect, useState } from 'react';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, PieChart, Pie, Cell, Legend,
} from 'recharts';
import { TrendingUp, Wallet, Receipt, AlertTriangle, Scissors, Package, DollarSign, Landmark } from 'lucide-react';
import api from '../lib/api';
import { PageHeader, StatCard, Card, Badge, EmptyState } from '../components/ui';
import { fmtMoney, fmtNum, fmtDate } from '../lib/utils';
import { useAuth } from '../context/AuthContext';

const PIE_COLORS = ['#1E3A8A', '#B45309', '#0891B2', '#65A30D', '#7C3AED', '#DB2777'];

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    api.get('/dashboard/stats').then((r) => setStats(r.data));
    api.get('/stock-activities').then((r) => setActivities(r.data.slice(0, 8)));
  }, []);

  if (!stats) return <div className="text-slate-400 animate-pulse">Loading dashboard…</div>;

  return (
    <div>
      <PageHeader
        title={`Welcome, ${user.name.split(' ')[0]}`}
        subtitle="Business overview · Khayyat Al-Babji Lil-Sayyidat"
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-sales" label="Total Sales" value={fmtMoney(stats.totalSales)} icon={TrendingUp} sub={`${stats.invoiceCount} invoices`} />
        <StatCard testid="stat-received" label="Collected" value={fmtMoney(stats.totalReceived)} icon={DollarSign} accent="text-emerald-600" />
        <StatCard testid="stat-due" label="Receivables" value={fmtMoney(stats.totalDue)} icon={Landmark} accent="text-[#B45309]" sub="Outstanding" />
        <StatCard testid="stat-profit" label="Net Profit" value={fmtMoney(stats.netProfit)} icon={Wallet} accent={stats.netProfit >= 0 ? 'text-emerald-600' : 'text-rose-600'} sub={`Expenses ${fmtMoney(stats.totalExpenses)}`} />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-jobs" label="Active Jobs" value={fmtNum(stats.activeJobs)} icon={Scissors} accent="text-blue-600" />
        <StatCard testid="stat-goods" label="Finished Goods" value={fmtNum(stats.finishedGoods)} icon={Package} accent="text-cyan-600" sub="dozens in stock" />
        <StatCard testid="stat-lowraw" label="Low Raw Material" value={fmtNum(stats.lowStockRaw.length)} icon={AlertTriangle} accent="text-rose-600" sub={stats.lowStockRaw.join(', ') || 'All good'} />
        <StatCard testid="stat-lowprod" label="Low Products" value={fmtNum(stats.lowStockProducts.length)} icon={AlertTriangle} accent="text-amber-600" sub={stats.lowStockProducts.join(', ') || 'All good'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-4 lg:col-span-2">
          <h3 className="text-sm font-heading font-bold text-slate-800 mb-4">Sales Trend</h3>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={stats.salesTrend} margin={{ left: -18, right: 8, top: 4 }}>
              <defs>
                <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#1E3A8A" stopOpacity={0.3} />
                  <stop offset="100%" stopColor="#1E3A8A" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#Eef2f7" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} tickFormatter={(d) => fmtDate(d).slice(0, 6)} />
              <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} />
              <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #E2E8F0' }} />
              <Area type="monotone" dataKey="amount" stroke="#1E3A8A" strokeWidth={2} fill="url(#g1)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-4">
          <h3 className="text-sm font-heading font-bold text-slate-800 mb-4">Expenses by Category</h3>
          {stats.expenseBreakdown.length ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={stats.expenseBreakdown} dataKey="amount" nameKey="category" cx="50%" cy="45%" outerRadius={80} innerRadius={45}>
                  {stats.expenseBreakdown.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyState title="No expenses yet" icon={Wallet} />}
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-4">
          <h3 className="text-sm font-heading font-bold text-slate-800 mb-4">Top Products (units sold)</h3>
          {stats.topProducts.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={stats.topProducts} layout="vertical" margin={{ left: 20, right: 16 }}>
                <XAxis type="number" tick={{ fontSize: 11, fill: '#94A3B8' }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} width={90} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6 }} />
                <Bar dataKey="units" fill="#B45309" radius={[0, 4, 4, 0]} barSize={16} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyState title="No sales yet" icon={Receipt} />}
        </Card>

        <Card className="p-4 lg:col-span-2">
          <h3 className="text-sm font-heading font-bold text-slate-800 mb-3">Recent Stock Activity</h3>
          <div className="divide-y divide-slate-100">
            {activities.map((a) => (
              <div key={a.id} className="flex items-center justify-between py-2.5 text-sm" data-testid="activity-row">
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 truncate">{a.itemName} {a.size ? <span className="text-slate-400">· {a.size}</span> : ''}</p>
                  <p className="text-xs text-slate-400 truncate">{a.notes} · {a.user}</p>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <span className={`num font-semibold ${a.quantity >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {a.quantity >= 0 ? '+' : ''}{a.quantity}
                  </span>
                  <p className="text-xs text-slate-400">{fmtDate(a.date)}</p>
                </div>
              </div>
            ))}
            {!activities.length && <EmptyState title="No activity" icon={Package} />}
          </div>
        </Card>
      </div>
    </div>
  );
}
